import { useEffect, useState } from "react";

// Shared "weather display mode" preference for the weather chip (2026-06-22).
// Lets the office pin the chip's resting value to a single metric (rain / temp
// / wind) instead of the default rotating cycle. Stored in localStorage so it
// persists across reloads and rides along on every surface that renders a
// WeatherChip (week grid + Create/Edit event modals). A same-tab CustomEvent
// keeps live listeners in sync (storage events only fire cross-tab), and the
// `storage` event covers other tabs.

const STORAGE_KEY = "bdr_weather_display_mode";
const CHANGE_EVENT = "bdr-weather-display-mode-change";
const DEFAULT_MODE = "cycle";

export const WEATHER_DISPLAY_MODES = [
  { value: "cycle", label: "Cycle" },
  { value: "rain", label: "Rain %" },
  { value: "temp", label: "Temp" },
  { value: "wind", label: "Wind" },
];

const VALID = new Set(WEATHER_DISPLAY_MODES.map((m) => m.value));

export const getWeatherDisplayMode = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && VALID.has(v) ? v : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

export const setWeatherDisplayMode = (mode) => {
  const next = VALID.has(mode) ? mode : DEFAULT_MODE;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage unavailable (private mode / quota) — still notify same-tab
    // listeners so the UI updates for this session.
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  } catch {
    // CustomEvent unsupported — listeners just won't live-update; harmless.
  }
};

/**
 * Current weather display mode, live. Subscribes to the same-tab CustomEvent
 * (fired by setWeatherDisplayMode) AND the cross-tab `storage` event so every
 * surface stays in sync without prop drilling.
 */
export const useWeatherDisplayMode = () => {
  const [mode, setMode] = useState(getWeatherDisplayMode);

  useEffect(() => {
    const onChange = () => setMode(getWeatherDisplayMode());
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setMode(getWeatherDisplayMode());
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return mode;
};
